import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Link, router } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { useSortable } from '@/hooks/useSortable'

interface Webhook {
  id: number
  name: string
  description: string | null
  token: string
  active: boolean
  project_name: string | null
  responsible_agent_username: string
  creator_agent_username: string
  inbound_url: string
  event_count: number
  pending_event_count: number
  created_at: string
}

interface ProjectRef {
  id: number
  name: string
}

interface Filters {
  project_id: number | null
}

interface Props {
  webhooks: Webhook[]
  projects: ProjectRef[]
  filters: Filters
}

const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'active', label: 'Status' },
  { value: 'responsible_agent_username', label: 'Agent' },
  { value: 'event_count', label: 'Events' },
  { value: 'pending_event_count', label: 'Pending' },
  { value: 'created_at', label: 'Created' },
]

export default function Index({ webhooks, projects, filters }: Props) {
  const { sorted, order, orderBy, handleSort } = useSortable(webhooks, 'name')
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Webhook | null>(null)

  function applyFilter(key: string, value: any) {
    const newFilters: Record<string, any> = { ...filters, [key]: value || undefined }
    Object.keys(newFilters).forEach(k => {
      if (newFilters[k] === null || newFilters[k] === undefined || newFilters[k] === '' || newFilters[k] === 0) {
        delete newFilters[k]
      }
    })
    router.get('/manage/webhooks/webhooks', newFilters, { preserveState: true })
  }

  function handleDelete() {
    if (deleteTarget) {
      router.delete(`/manage/webhooks/webhooks/${deleteTarget.id}`)
      setDeleteTarget(null)
    }
  }

  function handleCopyUrl(webhook: Webhook) {
    navigator.clipboard.writeText(webhook.inbound_url)
    setCopiedId(webhook.id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  return (
    <SidebarLayout
      section="manage"
      title="Webhooks"
      action={
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button component={Link} href="/monitoring/webhook_events" variant="outlined">
            Event log
          </Button>
          <Button component={Link} href="/manage/webhooks/webhooks/new" variant="contained">
            New webhook
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Project</InputLabel>
          <Select
            value={filters.project_id || ''}
            label="Project"
            onChange={e => applyFilter('project_id', e.target.value || null)}
          >
            <MenuItem value="">All Projects</MenuItem>
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {filters.project_id && (
          <Button
            size="small"
            onClick={() => router.get('/manage/webhooks/webhooks', {}, { preserveState: true })}
          >
            Clear
          </Button>
        )}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Sort by</InputLabel>
          <Select
            value={orderBy}
            label="Sort by"
            onChange={e => handleSort(e.target.value)}
          >
            {SORT_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton size="small" onClick={() => handleSort(orderBy)}>
          {order === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
        </IconButton>
      </Box>

      {sorted.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
          No webhooks found. Create one to start receiving events.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {sorted.map((webhook) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={webhook.id}>
              <Paper
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1, mr: 1 }}>
                    {webhook.name}
                  </Typography>
                  <Chip
                    label={webhook.active ? 'Active' : 'Inactive'}
                    size="small"
                    variant="outlined"
                    color={webhook.active ? 'success' : 'default'}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Agent: {webhook.responsible_agent_username}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.7rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flexGrow: 1,
                    }}
                  >
                    {webhook.inbound_url}
                  </Typography>
                  <Tooltip title={copiedId === webhook.id ? 'Copied!' : 'Copy URL'}>
                    <IconButton size="small" onClick={() => handleCopyUrl(webhook)}>
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                  <Chip label={`${webhook.event_count} events`} size="small" variant="outlined" />
                  {webhook.pending_event_count > 0 ? (
                    <Chip label={`${webhook.pending_event_count} pending`} size="small" color="warning" />
                  ) : (
                    <Chip label="0 pending" size="small" variant="outlined" />
                  )}
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5 }}>
                  Created {new Date(webhook.created_at).toLocaleDateString()}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                  <Button component={Link} href={`/manage/webhooks/webhooks/${webhook.id}/edit`} size="small">
                    Edit
                  </Button>
                  <Button size="small" color="error" onClick={() => setDeleteTarget(webhook)}>
                    Delete
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete webhook"
        message={`Are you sure you want to delete webhook '${deleteTarget?.name}'? This will also delete all associated events.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </SidebarLayout>
  )
}
